import React, { useState, useEffect, useCallback } from 'react'
import {
    Autocomplete,
    InputLabel,
    TextField,
    debounce,
    InputAdornment,
    IconButton,
    AutocompleteInputChangeReason,
    FormHelperText,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { locationChangeType } from '../../constants/constants'

interface IPlaceType {
    description: string
    place_id: string
}

export type PlaceChangeType =
    (typeof locationChangeType)[keyof typeof locationChangeType]

interface IAutoSuggestPlacesProps {
    value: string
    onPlaceSelected: (place: IPlaceType, type: PlaceChangeType) => void
    disableEdit?: boolean
}

declare global {
    interface Window {
        google: any
    }
}

/**
 * TODO:
 * In future try to lift the selectedPlace, inputValue state to parent component. I think it will help in managing the state better and avoid unnecessary re-renders.
 * Also I feel one of the useEffect can be removed by doing that. I also think that we can manage both the selectedPlace and inputValue in one single state. We don't
 * need to have both of them separately.
 */
const AutoSuggestPlaces: React.FC<IAutoSuggestPlacesProps> = ({
    value,
    onPlaceSelected,
    disableEdit = false,
}) => {
    const { t } = useTranslation()
    const [inputValue, setInputValue] = useState(value)
    const [options, setOptions] = useState<IPlaceType[]>([])
    const [selectedPlace, setSelectedPlace] = useState<IPlaceType | null>(null)
    const [hasSearched, setHasSearched] = useState(false)
    const [showSearchIcon, setShowSearchIcon] = useState(false)
    const [showClearIcon, setShowClearIcon] = useState(false)

    const debouncedFetchSuggestions = useCallback(
        debounce((query: string) => {
            if (query?.length < 3) {
                setOptions([])
                setHasSearched(false)
                return
            }
            setHasSearched(false)
            const service = new window.google.maps.places.AutocompleteService()
            service.getQueryPredictions(
                { input: query },
                (
                    predictions:
                        | google.maps.places.QueryAutocompletePrediction[]
                        | null,
                    status: google.maps.places.PlacesServiceStatus,
                ) => {
                    if (!selectedPlace?.description) {
                        setHasSearched(true)
                    }
                    if (
                        status !== google.maps.places.PlacesServiceStatus.OK ||
                        !predictions
                    ) {
                        console.error(status)
                        setOptions([])
                        return
                    }
                    const updatedPredictions = predictions.map((item) => ({
                        description: item.description,
                        place_id: item.place_id,
                    }))
                    setOptions(updatedPredictions as unknown as IPlaceType[])
                },
            )
        }, 300),
        [],
    )

    /**
     * Handle input change and trigger debounced fetch
     */
    useEffect(() => {
        debouncedFetchSuggestions(inputValue)
    }, [inputValue])

    /**
     * Sync inputValue with the parent-provided value
     */
    useEffect(() => {
        setInputValue(value)
        if (value) {
            setSelectedPlace({
                description: value,
                place_id: '',
            })
        } else {
            setSelectedPlace(null)
        }
    }, [value])

    /**
     * Handle input change in Autocomplete
     */
    const handleInputChange = useCallback(
        (
            _: any,
            newInputValue: string,
            reason: AutocompleteInputChangeReason,
        ) => {
            if (reason === 'reset') {
                return
            }
            setInputValue(newInputValue || '')
            setHasSearched(false) // Reset search state when input changes
            setSelectedPlace(null) // Clear selected place when input changes
            setShowSearchIcon((newInputValue?.length ?? 0) > 0)
            setShowClearIcon(false)
            // Always notify parent of changes to allow clearing coordinates
            onPlaceSelected(
                { description: newInputValue || '', place_id: '' },
                locationChangeType.INPUT_EVENT,
            )
        },
        [onPlaceSelected],
    )

    /**
     * Handle option selection in Autocomplete
     */
    const handleOptionSelected = useCallback(
        (_: any, value: IPlaceType | null | string) => {
            // Always hide helper text when Enter is pressed, regardless of value
            setHasSearched(false)
            setShowSearchIcon(false)

            if (typeof value === 'string') {
                // Handle custom input (freeSolo)
                const customPlace: IPlaceType = {
                    description: value,
                    place_id: '',
                }
                setSelectedPlace(customPlace)
                setShowClearIcon(true)
                onPlaceSelected(customPlace, locationChangeType.OPTION_SELECTED)
            } else {
                setSelectedPlace(value)
                setShowClearIcon(!!value)
                if (value) {
                    onPlaceSelected(value, locationChangeType.OPTION_SELECTED)
                }
            }
        },
        [onPlaceSelected],
    )

    const handleSearchIconClick = useCallback(() => {
        handleOptionSelected(null, inputValue)
    }, [handleOptionSelected, inputValue])

    return (
        <Autocomplete
            options={options}
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.description
            }
            onInputChange={handleInputChange}
            onChange={handleOptionSelected}
            value={selectedPlace}
            freeSolo
            id={'search-address'}
            disableClearable={!showClearIcon}
            disabled={disableEdit}
            slotProps={{
                clearIndicator: {
                    id: 'search-address-clear-button',
                },
                popupIndicator: {
                    id: 'search-address-dropdown-button',
                },
            }}
            renderInput={(params) => (
                <>
                    <InputLabel
                        sx={{
                            opacity: '0.7',
                            fontSize: '14px',
                            marginBottom: '7px',
                        }}
                    >
                        {t('searchLabel')}
                    </InputLabel>
                    <TextField
                        title={inputValue}
                        {...params}
                        value={inputValue}
                        placeholder={t('searchPlaceholder')}
                        variant="outlined"
                        fullWidth
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleOptionSelected(null, inputValue)
                            }
                        }}
                        onBlur={() => handleOptionSelected(null, inputValue)}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {showSearchIcon && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleSearchIconClick}
                                                id="search-address-search-button"
                                            >
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                    {options.length === 0 &&
                        inputValue.length >= 3 &&
                        hasSearched && (
                            <FormHelperText
                                sx={{
                                    color: '#1976d2',
                                    fontSize: '12px',
                                    marginTop: '4px',
                                }}
                            >
                                {t('pressEnterToUseThisAddress')}
                            </FormHelperText>
                        )}
                </>
            )}
        />
    )
}
const NamedAutoSuggestPlaces = React.memo(AutoSuggestPlaces)
export default NamedAutoSuggestPlaces
